import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Mail, Clock, CheckCircle, XCircle, Trash2, Plus, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { companyService } from '@/services/companyService';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useGlobalToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type { CompanyInvitation } from '@/types';

export function CompanyInvitationsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { success, error } = useGlobalToast();
  const [invitations, setInvitations] = useState<CompanyInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const isCompanyOwner = companyService.isCompanyOwner(user! as any);

  useEffect(() => {
    if (!isCompanyOwner) {
      navigate('/dashboard');
    } else {
      loadInvitations();
    }
  }, [user?.id, isCompanyOwner, navigate]);

  const loadInvitations = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const data = await companyService.getCompanyInvitations(user.id);
      setInvitations(data);
    } catch (err) {
      error('Failed to load invitations', 'Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendInvitation = async (invitationData: any) => {
    try {
      await companyService.sendInvitation(invitationData);
      success('Invitation sent', `Invitation sent to ${invitationData.inviteeEmail}`);
      setShowInviteModal(false);
      await loadInvitations();
    } catch (err) {
      error('Failed to send invitation', 'Please check the email and try again.');
    }
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    if (!window.confirm("Are you sure you want to delete this invitation?")) return;
    // Delete invitation logic
  };


  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: CompanyInvitation['status']) => {
    switch (status) {
      case 'pending': return <Badge variant="warning">Pending</Badge>;
      case 'accepted': return <Badge variant="success">Accepted</Badge>;
      case 'rejected': return <Badge variant="danger">Rejected</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Team', href: '/team' },
    { label: 'Invitations' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Loading invitations...</p>
        </div>
      </div>
    );
  }

  if (!isCompanyOwner) return null;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-8">
          <Breadcrumb items={breadcrumbItems} onBack={() => navigate('/team')} />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/team')} size="sm" icon={<ArrowLeft className="w-4 h-4" />}>Back to Team</Button>
            <Button variant="primary" onClick={() => setShowInviteModal(true)} size="sm" icon={<Plus className="w-4 h-4" />}>Invite Member</Button>
          </div>
        </div>

        <div className="page-header mt-4">
          <h1 className="page-title">Team Invitations</h1>
          <p className="page-description">Manage outstanding and past invitations to join your company.</p>
        </div>

        <Card>
          {invitations.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No invitations found</h3>
              <p className="text-gray-500 mb-6">You haven't sent any invitations yet.</p>
              <Button variant="primary" onClick={() => setShowInviteModal(true)}>Send First Invitation</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase">Email</th>
                    <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase">Role</th>
                    <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase">Rate</th>
                    <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase">Sent</th>
                    <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invitations.map((invitation) => (
                    <tr key={invitation.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">{invitation.inviteeEmail}</td>
                      <td className="py-4 px-6 text-sm text-gray-600 capitalize">{invitation.companyRole}</td>
                      <td className="py-4 px-6 text-sm text-gray-600 capitalize">{invitation.employmentType}</td>
                      <td className="py-4 px-6 text-sm text-gray-600">€{invitation.proposedHourlyRate}/hr</td>
                      <td className="py-4 px-6 text-sm text-gray-600">{formatDate(invitation.createdAt)}</td>
                      <td className="py-4 px-6">
                        {getStatusBadge(invitation.status)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        {invitation.status === 'pending' && (
                          <button
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            onClick={() => handleDeleteInvitation(invitation.id)}
                            title="Revoke Invitation"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {showInviteModal && (
          <InvitationModal
            isOpen={showInviteModal}
            onClose={() => setShowInviteModal(false)}
            onSend={handleSendInvitation}
          />
        )}
      </main>
    </div>
  );
}

function InvitationModal({ isOpen, onClose, onSend }: any) {
  const [formData, setFormData] = useState({
    inviteeEmail: '',
    companyRole: 'technician' as const,
    employmentType: 'employee' as const,
    proposedHourlyRate: 35,
    proposedCommissionRate: 15, // Default
    canTakePersonalJobs: true,
    permissions: ['view_jobs', 'update_status'],
    message: ''
  });

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSend(formData);
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permission]
        : prev.permissions.filter(p => p !== permission)
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-900">Invite Team Member</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
            <Plus className="w-5 h-5 rotate-45" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
              placeholder="colleague@example.com"
              value={formData.inviteeEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, inviteeEmail: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                value={formData.companyRole}
                onChange={(e) => setFormData(prev => ({ ...prev, companyRole: e.target.value as any }))}
              >
                <option value="technician">Technician</option>
                <option value="manager">Manager</option>
                <option value="apprentice">Apprentice</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                value={formData.employmentType}
                onChange={(e) => setFormData(prev => ({ ...prev, employmentType: e.target.value as any }))}
              >
                <option value="employee">Employee</option>
                <option value="contractor">Contractor</option>
                <option value="freelancer">Freelancer</option>
                <option value="partner">Partner</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate (€)</label>
              <input
                type="number"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                value={formData.proposedHourlyRate}
                onChange={(e) => setFormData(prev => ({ ...prev, proposedHourlyRate: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commission (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                value={formData.proposedCommissionRate}
                onChange={(e) => setFormData(prev => ({ ...prev, proposedCommissionRate: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
            <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
              {[
                { id: 'view_jobs', label: 'View Jobs' },
                { id: 'update_status', label: 'Update Job Status' },
                { id: 'communicate_customers', label: 'Message Customers' },
                { id: 'manage_team', label: 'Manage Team' },
                { id: 'view_analytics', label: 'View Analytics' }
              ].map(perm => (
                <label key={perm.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={formData.permissions.includes(perm.id)}
                    onChange={(e) => handlePermissionChange(perm.id, e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">{perm.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Personal Note (Optional)</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm min-h-[80px]"
              placeholder="Add a personal message..."
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary">Send Invitation</Button>
          </div>
        </form>
      </div>
    </div>
  );
}